import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// Shared small utilities used across the app.
// Keep these minimal and well-documented so components can stay focused on UI.
const MANILA_TIME_ZONE = "Asia/Manila";

type MaybeDate = string | Date | null | undefined;

function parseDateString(value: string): Date {
  const trimmed = value.trim();
  if (!trimmed) return new Date(NaN);

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split("-").map(Number);
    return new Date(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T12:00:00+08:00`);
  }

  return new Date(trimmed);
}

function parseDate(v?: MaybeDate): Date | null {
  if (v === undefined || v === null || v === "") return null;
  const d = typeof v === "string" ? parseDateString(v) : v;
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() <= 1900) return null;
  return d;
}

export function parseApiDate(v?: MaybeDate): Date | null {
  return parseDate(v);
}

export const unwrap = (resp: any): any[] => {
  const d = resp?.data ?? resp;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
};

export const unwrapOne = (resp: any): any => {
  const d = resp?.data ?? resp;
  if (Array.isArray(d)) return d[0] ?? null;
  if (d && d.data !== undefined) {
    if (Array.isArray(d.data)) return d.data[0] ?? null;
    return d.data ?? null;
  }
  return d ?? null;
};

// Coordinate (latitude/longitude) precision: max 10 decimal places across the app.
export const MAX_COORD_DECIMALS = 10;

// Truncate (not round) a coordinate string to at most MAX_COORD_DECIMALS decimal places.
// Accepts intermediate typing states (e.g. "13.", "-", "") and returns a string for inputs.
export const clampCoordinateInput = (raw: string): string => {
  if (raw === null || raw === undefined) return "";
  let s = String(raw).trim();
  // keep only valid coordinate characters
  s = s.replace(/[^0-9.\-]/g, "");
  // only one leading minus
  s = s.replace(/(?!^)-/g, "");
  // only one dot
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    const [intPart, decPart = ""] = s.split(".");
    s = intPart + "." + decPart.slice(0, MAX_COORD_DECIMALS);
  }
  return s;
};

// Truncate a numeric coordinate to at most MAX_COORD_DECIMALS decimals (no rounding bias beyond fixed).
export const clampCoordinateNumber = (n: any): number => {
  const num = Number(n);
  if (!isFinite(num)) return 0;
  const factor = Math.pow(10, MAX_COORD_DECIMALS);
  return Math.trunc(num * factor) / factor;
};

/** Validate a non-empty string looks like an email (contains @ and a dot after it).
 * Empty values are treated as valid (optional fields). This regex explicitly allows
 * Unicode letters such as ñ and Ñ, unlike browser `type="email"` validation.
 */
export const isValidEmail = (value?: string | null): boolean => {
  if (!value || !value.trim()) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

// Treat 1899/1900 (and 0001) as "default" sentinel dates returned by some backends.
// When detected, display helpers return "" so the UI shows nothing instead of the placeholder year.
export const isDefaultDate = (value: any): boolean => {
  if (value === null || value === undefined || value === "") return true;
  try {
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return true;
      if (s.startsWith("1899") || s.startsWith("1900") || s.startsWith("0001")) return true;
    }
    const d = value instanceof Date ? value : new Date(String(value));
    if (isNaN(d.getTime())) return false;
    const y = d.getFullYear();
    return y === 1899 || y === 1900 || y <= 1;
  } catch {
    return false;
  }
};

export function formatLongDate(v?: MaybeDate, placeholder = ""): string {
  const d = parseDate(v);
  if (!d) return placeholder;
  return d.toLocaleDateString("en-US", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** App-wide empty-date placeholder shown when the value is missing or the 1900 sentinel. */
export const EMPTY_DATE_PLACEHOLDER = "dd/MM/yyyy";

export function formatDate(v?: MaybeDate, placeholder = EMPTY_DATE_PLACEHOLDER): string {
  const d = parseDate(v);
  if (!d) return placeholder;
  // App-wide display format: dd/MM/yyyy
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

export function formatDateTime(v?: MaybeDate, placeholder = ""): string {
  const d = parseDate(v);
  if (!d) return placeholder;
  return d.toLocaleString("en-US", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateTimeLong(v?: MaybeDate, placeholder = ""): string {
  return formatDateTime(v, placeholder);
}

export function formatHeaderDateTime(v?: MaybeDate): { date: string; time: string } {
  const d = parseDate(v) || new Date();
  return {
    date: d.toLocaleDateString("en-US", {
      timeZone: MANILA_TIME_ZONE,
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-US", {
      timeZone: MANILA_TIME_ZONE,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
}

export function convertToPhilippinesTime(v?: MaybeDate): string {
  const d = parseDate(v);
  if (!d) return "";
  return d.toLocaleString("en-US", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatTime(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (/H$/i.test(trimmed)) return trimmed.toUpperCase();
  const digits = trimmed.replace(/[:\s]/g, "");
  if (/^\d{3,4}$/.test(digits)) return `${digits.padStart(4, "0")}H`;
  return trimmed;
}

export function eventTimeToInput(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim().toUpperCase();
  if (/^\d{3,4}H$/.test(trimmed)) {
    const digits = trimmed.slice(0, -1).padStart(4, "0");
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    return trimmed.padStart(5, "0");
  }
  if (/^\d{3,4}$/.test(trimmed)) {
    const digits = trimmed.padStart(4, "0");
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }
  return "";
}

export function eventTimeFromInput(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [h, m] = trimmed.split(":");
    return `${h.padStart(2, "0")}${m}H`;
  }
  const normalized = trimmed.toUpperCase();
  if (/^\d{3,4}H$/.test(normalized)) return normalized;
  if (/^\d{3,4}$/.test(normalized)) return `${normalized.padStart(4, "0")}H`;
  return normalized;
}

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:?\d{2})?$/;

export function toDateInput(v?: MaybeDate): string {
  if (typeof v === "string" && DATE_INPUT_PATTERN.test(v)) {
    return v;
  }
  const d = parseDate(v);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

export function toManilaDateTimeIso(v?: MaybeDate): string {
  if (!v) return "";
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (DATE_INPUT_PATTERN.test(trimmed)) {
      return `${trimmed}T12:00:00+08:00`;
    }
    if (DATE_TIME_ISO_PATTERN.test(trimmed)) {
      const d = parseDate(trimmed);
      if (!d) return trimmed;
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: MANILA_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(d);
      const year = parts.find((part) => part.type === "year")?.value ?? d.getFullYear();
      const month = parts.find((part) => part.type === "month")?.value ?? String(d.getMonth() + 1).padStart(2, "0");
      const day = parts.find((part) => part.type === "day")?.value ?? String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}T12:00:00+08:00`;
    }
  }
  const d = parseDate(v);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const year = parts.find((part) => part.type === "year")?.value ?? d.getFullYear();
  const month = parts.find((part) => part.type === "month")?.value ?? String(d.getMonth() + 1).padStart(2, "0");
  const day = parts.find((part) => part.type === "day")?.value ?? String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T12:00:00+08:00`;
}

export function getTodayInManila(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function isEmptyDate(v?: MaybeDate): boolean {
  return parseDate(v) === null;
}

// ========================
// Default (sentinel) date handling
// ========================
// The backend uses 01/01/1900 as the "no value" sentinel for dates.
export const DEFAULT_DATE_INPUT = "1900-01-01"; // value for <input type="date">
export const DEFAULT_DATE_DISPLAY = "01/01/1900";
export const DEFAULT_DATE_ISO = "1900-01-01T00:00:00";

/**
 * Value for a date input. The 01/01/1900 sentinel (or any year <= 1900) is
 * treated as "no value" so the input renders empty (browser shows dd/mm/yyyy).
 * On save, the empty value is sent back as the 01/01/1900 default.
 */
export function toDateInputOrDefault(v?: MaybeDate): string {
  if (isDefaultDate(v)) return "";
  return toDateInput(v);
}

/** Value to send on save: preserve the selected calendar date in Manila time and
 * avoid timezone drift for date-only fields. Empty/default dates still use the
 * backend sentinel.
 */
export function toIsoOrDefault(v?: MaybeDate): string {
  const d = parseDate(v);
  if (!d) return DEFAULT_DATE_ISO;
  return toManilaDateTimeIso(v) || DEFAULT_DATE_ISO;
}

/** Required date fields must be a real date (year must be greater than 1900). */
export function isRequiredDateValid(v?: MaybeDate): boolean {
  return parseDate(v) !== null;
}

export function formatDateRange(from?: MaybeDate, to?: MaybeDate): string {
  if (!from) return "";
  const fromLabel = formatDate(from);
  if (!to || String(from) === String(to)) return fromLabel;
  return `${fromLabel} - ${formatDate(to)}`;
}

// Convert API member payload into the partial MemberDetailModel used in UI.
// (normalizeMemberPayload removed from shared utils - it is specific to the MyProfile page)

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ========================
// Location code formatter
// ========================
// Rule: if a location code contains any alphabetic character, uppercase the
// whole string. If it is purely numeric, leave it untouched.
// Used before saving Region / Province / City / Barangay codes.
export function toLocationCode(raw: string | null | undefined): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  return /[a-zA-Z]/.test(s) ? s.toUpperCase() : s;
}

// ========================
// Shared helpers
// ========================
const safeString = (v: any) => (v ?? "").toString().trim();

const isValidNumber = (v: any) => !isNaN(Number(v));

const safeNumber = (v: any, fallback = 0) => (isValidNumber(v) ? Number(v) : fallback);

const toDate = (input?: string | Date | number | null) => {
  if (!input) return null;

  if (input instanceof Date) return input;

  const v = String(input);

  if (/\/Date\((-?\d+)\)\//.test(v)) {
    return new Date(Number(RegExp.$1));
  }

  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// ========================
// String utilities
// ========================
export function shortenMiddle(s?: string | null, max = 18) {
  const str = safeString(s);
  if (!str) return "";
  if (str.length <= max) return str;

  const keep = Math.max(3, Math.floor((max - 3) / 2));
  return `${str.slice(0, keep)}...${str.slice(-keep)}`;
}

export function maskEmailForOtp(e?: string | null) {
  const email = safeString(e);
  if (!email) return "your email";

  const [local, domain] = email.split("@");
  if (!domain) return `${email.slice(0, 2)}***`;

  return `${local.slice(0, 2)}***@${domain}`;
}

// ========================
// Date formatting (merged logic)
// ========================
// ========================
// Number formatting base
// ========================
function formatNumberBase(value: number, locale = "en-PH", min = 0, max = 2) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(value);
}

// ========================
// Number utilities (merged style)
// ========================
export function formatCurrency(
  value?: number | null,
  opts?: { locale?: string; symbol?: string; min?: number; max?: number },
) {
  const v = safeNumber(value);
  const { locale = "en-PH", symbol = "₱", min = 2, max = 2 } = opts || {};

  return `${symbol}${formatNumberBase(v, locale, min, max)}`;
}

export function formatCurrencyShort(
  value?: number | null,
  opts?: { locale?: string; symbol?: string },
) {
  const v = safeNumber(value);
  const { symbol = "₱" } = opts || {};

  if (Math.abs(v) >= 1000) return `${symbol}${(v / 1000).toFixed(1)}K`;

  return formatCurrency(v, { symbol, min: 0, max: 2 });
}

export function formatCount(v?: number | null, locale = "en-PH") {
  return safeNumber(v).toLocaleString(locale, {
    maximumFractionDigits: 0,
  });
}

export function formatWeight(v?: number | null, locale = "en-PH", digits = 2) {
  return safeNumber(v).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatNumber(v?: number | null, locale = "en-PH", digits = 2) {
  const num = safeNumber(v);

  const rounded = Number(num.toFixed(digits));
  if (Math.abs(rounded - Math.round(rounded)) < 0.001) {
    return Math.round(rounded).toLocaleString(locale);
  }

  return rounded.toLocaleString(locale);
}

export function formatPercent(v?: number | null, digits = 2) {
  const num = safeNumber(v);
  const r = Number(num.toFixed(digits));
  return Math.abs(r - Math.round(r)) < 0.001 ? `${Math.round(r)}%` : `${r}%`;
}

export function formatDecimal(v?: number | null, digits = 2) {
  const num = safeNumber(v);
  const r = Number(num.toFixed(digits));

  return Math.abs(r - Math.round(r)) < 0.001 ? String(Math.round(r)) : String(r);
}

// ========================
// Misc utilities
// ========================
export function displayPersonName(name?: string | null, id?: number | null) {
  if (safeString(name)) return name!;
  if (id === 0) return "";
  if (id) return `User #${id}`;
  return "";
}

export function secondsToMMSS(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatCoordinates(lat?: number | null, lng?: number | null, digits = 6) {
  if (lat == null || lng == null) return "—";
  return `${formatDecimal(lat, digits)}, ${formatDecimal(lng, digits)}`;
}

// ========================
// Clipboard
// ========================
export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch (e) {
      // fallback copy failed

      console.debug(e);
      return false;
    }
  }
}

// ========================
// Name utilities
// ========================
export function getMiddleInitials(mn?: string) {
  const str = String(mn ?? "").trim();
  if (!str) return "";

  return str
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w?.[0] ?? "").toUpperCase())
    .join("");
}

export function getInitials(input?: string, max = 2, strip = false) {
  const str = String(input ?? "").trim();
  if (!str) return "--";

  const words = str
    .split(/\s+/)
    .map((w) => (strip ? w.replace(/[^\p{L}\p{N}]/gu, "") : w))
    .filter(Boolean);

  if (!words.length) return str.slice(0, max).toUpperCase();

  if (words.length === 1) {
    return words[0].slice(0, max).toUpperCase();
  }

  return words
    .slice(0, max)
    .map((w) => (w?.[0] ?? "").toUpperCase())
    .join("");
}

export function buildFullname(
  firstname?: string,
  middlename?: string,
  lastname?: string,
  suffix?: string,
) {
  const mi = getMiddleInitials(middlename);

  return [firstname, mi, lastname, suffix].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
}

// ========================
// Mobile utilities
// ========================
export function normalizeMobile(raw?: string) {
  let d = safeString(raw).replace(/\D/g, "");

  if (d.startsWith("0") && d.length >= 11) d = "63" + d.slice(1);
  else if (d.length === 10) d = "63" + d;

  return d.slice(0, 12);
}

export const isValidPhilMobile = (m?: string) => /^63\d{10}$/.test(safeString(m));

// ========================
// File / binary helpers
// Centralize common file/byte helpers so other libs can re-export from here.
// ========================
export async function fileToUint8Array(file: Blob): Promise<Uint8Array> {
  const arrayBuffer = await file.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

export function dataUrlToBytes(dataUrl: string): { bytes: Uint8Array; mime: string } {
  const match = dataUrl.match(/^data:(.*);base64,(.*)$/);
  if (!match) throw new Error("Invalid data URL");
  const mime = match[1];
  const b64 = match[2];
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return { bytes, mime };
}

export function descriptorToBytes(descriptor: number[]): Uint8Array {
  return new Uint8Array(new Float32Array(descriptor).buffer);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

export async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await fileToDataUrl(file);
  const [, b64] = dataUrl.split(",");
  return b64 ?? "";
}

export function bytesForWire(u8: Uint8Array): Uint8Array {
  return Array.from(u8) as unknown as Uint8Array;
}

/**
 * Convert stored image binary + mime type into a data URL suitable for <img src="..." />.
 * Accepts either a Uint8Array or an Array<number> (the project often uses Array for wire transport).
 */
export function imageDataToDataUrl(
  filetype?: string | null,
  imagedata?: Uint8Array | number[] | string | null,
): string | null {
  try {
    if (!imagedata) return null;

    const mime = filetype && String(filetype).trim() !== "" ? String(filetype) : "image/jpeg";

    // If the image data is already a data URL string, return it directly
    if (typeof imagedata === "string") {
      const s = imagedata.trim();
      if (!s) return null;
      if (s.startsWith("data:")) return s;

      // Treat string as a base64 payload (common when backend returns base64)
      // Ensure it looks like base64 (basic check) otherwise still return null
      // Allow padding and URL-safe base64 characters
      const base64Like = /^[A-Za-z0-9+/=\s-_]+$/.test(s);
      if (!base64Like) return null;
      // Remove whitespace/newlines if present
      const cleaned = s.replace(/\s+/g, "");
      return `data:${mime};base64,${cleaned}`;
    }

    // normalize arrays to Uint8Array
    const u8 = imagedata instanceof Uint8Array ? imagedata : new Uint8Array(imagedata as any);
    if (!u8 || u8.length === 0) return null;
    const b64 = bytesToBase64(u8);
    return `data:${mime};base64,${b64}`;
  } catch (e) {
    console.debug("failed to build data url from image data", e);
    return null;
  }
}

/**
 * Centralized avatar/logo source resolution:
 * - Prefer binary image stored as (filetype, imagedata)
 * - Then use common fields in order: imageDataUrl, profileurl, avatar, logourl, logoUrl
 * Returns a string suitable for <img src=... /> or null when none available.
 */
export function getAvatarSrc(entity?: any): string | null {
  if (!entity) return null;
  try {
    // PRIMARY: uploaded profile picture URL
    if (typeof entity.profileurl === "string") {
      const url = entity.profileurl.trim();
      if (url) return url;
    }

    // binary image on the entity (commonly returned by details endpoints)
    if (entity.filetype && entity.imagedata) {
      const by = imageDataToDataUrl(entity.filetype, entity.imagedata as any);
      if (by) return by;
    }

    // optional precomputed thumbnail
    if (typeof entity.imageDataUrl === "string" && entity.imageDataUrl) return entity.imageDataUrl;

    // common fallback fields
    if (typeof entity.avatar === "string" && entity.avatar.trim()) return entity.avatar.trim();
    if (typeof entity.logourl === "string" && entity.logourl.trim()) return entity.logourl.trim();
    if (typeof entity.logoUrl === "string" && entity.logoUrl.trim()) return entity.logoUrl.trim();

    return null;
  } catch (e) {
    console.debug("getAvatarSrc failed", e);
    return null;
  }
}

export function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ========================
// CSV helpers
// ========================
export function toCsv(rows: Record<string, any>[], headers?: string[]) {
  if (!Array.isArray(rows)) return "";
  if (rows.length === 0) return "";

  const keys: string[] = (() => {
    if (headers && headers.length > 0) return headers;
    const s = new Set<string>();
    rows.forEach((r) => {
      Object.keys(r || {}).forEach((k) => s.add(k));
    });
    return Array.from(s);
  })();

  const esc = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(",") || s.includes("\n")) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const headerLine = keys.join(",");
  const lines = rows.map((r) => keys.map((k) => esc(r[k])).join(","));
  return headerLine + "\n" + lines.join("\n");
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
